// Presentational library shell for sidebar navigation, mobile title bar, and routed content.

import type { ReactNode } from 'react';
import { useLocation } from 'react-router';
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '../components/ui/sidebar';
import { analyticsService } from '../services/analyticsService';

type LibraryLayoutProps = {
  sidebar: ReactNode;
  dialogs: ReactNode;
  announcement?: ReactNode;
  mobileTitle: string;
  children: ReactNode;
};

const LibraryLayout = ({
  sidebar,
  dialogs,
  announcement,
  mobileTitle,
  children,
}: LibraryLayoutProps) => {
  const location = useLocation();

  const trackDesktopSidebarChange = (open: boolean) => {
    analyticsService.trackSidebarToggle(open ? 'expanded' : 'collapsed', {
      surface: 'library_sidebar',
      source_path: location.pathname,
    });
  };

  const trackMobileSidebarChange = (open: boolean) => {
    analyticsService.trackNavigationMenuToggle(
      'library_mobile_sidebar',
      open ? 'open' : 'closed',
      {
        surface: 'library_sidebar',
        nav_source: 'mobile',
        is_mobile: true,
        source_path: location.pathname,
      }
    );
  };

  return (
    <SidebarProvider
      defaultOpen
      onOpenChange={trackDesktopSidebarChange}
      onMobileOpenChange={trackMobileSidebarChange}
    >
      {sidebar}

      <SidebarInset>
        <div className="flex h-svh min-h-0 flex-col">
          <div className="sticky top-0 z-20 flex items-center gap-3 border-b bg-background/95 px-4 py-3 backdrop-blur md:hidden">
            <SidebarTrigger />
            <div className="text-sm font-medium">{mobileTitle}</div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {announcement}
            {children}
          </div>
        </div>
      </SidebarInset>

      {dialogs}
    </SidebarProvider>
  );
};

export default LibraryLayout;
